/** @jsxImportSource ../i18n */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowRight, Check, Lock, Mail } from 'lucide-react';
import { register, clearError } from '../store/slices/authSlice';
import { AuthLayout } from '../components/shell/AuthLayout';
import { Banner, Button, Field, Input } from '../components/ui';
import { cx } from '../theme/cx';
import { useRegistrationOpen } from '../services/authOptions';

const Register = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error } = useSelector((state) => state.auth);
  const [formData, setFormData] = useState({ email: '', password: '', confirmPassword: '' });
  const [validationError, setValidationError] = useState('');
  const registrationOpen = useRegistrationOpen();

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const passwordRequirements = [
    { label: 'Au moins 8 caractères', met: formData.password.length >= 8 },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationError('');

    if (formData.password !== formData.confirmPassword) {
      setValidationError('Les mots de passe ne correspondent pas.');
      return;
    }

    if (formData.password.length < 8) {
      setValidationError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }

    const result = await dispatch(register({ email: formData.email, password: formData.password }));
    if (register.fulfilled.match(result)) {
      navigate('/dashboard');
    }
  };

  if (registrationOpen === false) {
    return (
      <AuthLayout>
        <h2 className="font-display text-display-md font-normal text-fg">Inscriptions fermées</h2>
        <p className="mt-3 text-[15px] leading-relaxed text-fg-muted">Cette instance de PEA Wallet n’accepte pas de nouveaux comptes.</p>
        <Button as={Link} to="/login" variant="secondary" className="mt-8">Retour à la connexion</Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <h2 className="font-display text-display-md font-normal text-fg">Créer votre compte</h2>
      <p className="mt-2 text-[15px] text-fg-muted">Suivez et analysez votre PEA dès aujourd’hui.</p>

      {(error || validationError) && <Banner tone="loss" role="alert" className="mt-6">{error || validationError}</Banner>}

      <form onSubmit={handleSubmit} className="mt-8 space-y-5">
        <Field label="Adresse email" htmlFor="register-email">
          <Input
            id="register-email"
            type="email"
            autoComplete="email"
            leadingIcon={Mail}
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="vous@exemple.fr"
            required
          />
        </Field>
        <Field label="Mot de passe" htmlFor="register-password">
          <Input
            id="register-password"
            type="password"
            autoComplete="new-password"
            leadingIcon={Lock}
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            placeholder="••••••••"
            required
          />
          <ul className="mt-2.5 space-y-1">
            {passwordRequirements.map((req) => (
              <li key={req.label} className={cx('flex items-center gap-2 text-xs transition-colors', req.met ? 'text-gain-300' : 'text-fg-subtle')}>
                <Check size={14} aria-hidden="true" className={req.met ? 'text-gain-400' : 'text-fg-faint'} />
                {req.label}
              </li>
            ))}
          </ul>
        </Field>
        <Field label="Confirmer le mot de passe" htmlFor="register-confirm">
          <Input
            id="register-confirm"
            type="password"
            autoComplete="new-password"
            leadingIcon={Lock}
            value={formData.confirmPassword}
            onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
            placeholder="••••••••"
            required
          />
        </Field>
        <Button type="submit" size="lg" fullWidth loading={loading} trailingIcon={ArrowRight}>
          Créer mon compte
        </Button>
      </form>

      <p className="mt-8 text-center text-sm text-fg-muted">
        Déjà un compte ?{' '}
        <Link to="/login" className="font-medium text-gold-300 transition-colors hover:text-gold-200">
          Se connecter
        </Link>
      </p>
    </AuthLayout>
  );
};

export default Register;
